import React, { useState, useEffect } from 'react';
import { expenseService } from '../services/expenseService';
import { jobService } from '../services/jobService';

const today = () => new Date().toISOString().slice(0, 10);

const ExpenseModal = ({ expense, defaultJobId, onClose, onSaved }) => {
  const [reasons, setReasons] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reasonId, setReasonId] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(today());
  const [jobId, setJobId] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = !!expense;

  useEffect(() => {
    expenseService.getReasons().then(res => setReasons(res.data?.data || [])).catch(() => {});
    jobService.getJobs().then(res => {
      const sorted = (res.data || []).slice().sort((a, b) => a.title.localeCompare(b.title));
      setJobs(sorted);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (expense) {
      setReasonId(String(expense.expense_reason_id));
      setAmount(String(expense.amount));
      setExpenseDate(expense.expense_date ? expense.expense_date.slice(0, 10) : today());
      setJobId(expense.job_id ? String(expense.job_id) : '');
      setDescription(expense.description || '');
    } else if (defaultJobId) {
      setJobId(String(defaultJobId));
    }
  }, [expense, defaultJobId]);

  const validate = () => {
    const errs = {};
    if (!reasonId) errs.reasonId = 'Please select a reason.';
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) errs.amount = 'Enter a valid amount.';
    if (!expenseDate) errs.expenseDate = 'Date is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setErrors({});
    try {
      const payload = {
        expense_reason_id: parseInt(reasonId),
        amount: parseFloat(amount),
        expense_date: expenseDate,
        job_id: jobId ? parseInt(jobId) : null,
        description: description.trim() || null,
      };
      let saved;
      if (isEdit) {
        saved = await expenseService.updateExpense(expense.id, payload);
      } else {
        saved = await expenseService.createExpense(payload);
      }
      onSaved(saved.data);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        setErrors(apiErrors);
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 rounded-lg border ${errors[field] ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Expense' : 'Add Expense'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                {errors.general}
              </div>
            )}

            {/* Expense Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={reasonId}
                onChange={(e) => { setReasonId(e.target.value); setErrors(p => ({ ...p, reasonId: undefined })); }}
                className={inputClass('reasonId')}
              >
                <option value="">— Select a reason —</option>
                {reasons.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              {errors.reasonId && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.reasonId}</p>}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount (£) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setErrors(p => ({ ...p, amount: undefined })); }}
                placeholder="0.00"
                className={`${inputClass('amount')} text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              />
              {errors.amount && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.amount}</p>}
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={expenseDate}
                onChange={(e) => { setExpenseDate(e.target.value); setErrors(p => ({ ...p, expenseDate: undefined })); }}
                className={inputClass('expenseDate')}
              />
              {errors.expenseDate && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.expenseDate}</p>}
            </div>

            {/* Job (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Job <span className="text-xs text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                className={inputClass('jobId')}
              >
                <option value="">— No job —</option>
                {jobs.map(j => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes <span className="text-xs text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Additional details..."
                className={`${inputClass('description')} resize-none`}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal;
